import { useQueryClient } from '@tanstack/react-query'
import { Loader2Icon } from 'lucide-react'
import { useState } from 'react'

import { Banner, Button, TextButton } from 'slab'
import {
  getGetEmployersAcaReportingNeedsAleConfirmationQueryKey,
  usePostEmployersAcaReportingConfirmAleStatus
} from 'utils/web_api'

import ACAReportingHeader from './ACAReportingHeader'
import AleQuestionSection from './AleQuestionSection'
import DownloadAndFileInstructions from './DownloadAndFileInstructions'

interface NonFilingEligibleViewProps {
  planYear: number
  aleStatus: boolean | null | undefined
  onChangeToOptIn?: () => void
}

export default function NonFilingEligibleView ({
  planYear,
  aleStatus: initialAleStatus,
  onChangeToOptIn
}: NonFilingEligibleViewProps) {
  const [aleStatus, setAleStatus] = useState<boolean | null>(initialAleStatus ?? null)
  const [confirmed, setConfirmed] = useState(initialAleStatus !== null && initialAleStatus !== undefined)
  const queryClient = useQueryClient()

  const formType = aleStatus ? '1095-C' : '1095-B'

  const confirmMutation = usePostEmployersAcaReportingConfirmAleStatus({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getGetEmployersAcaReportingNeedsAleConfirmationQueryKey({ plan_year: planYear })
        })
        setConfirmed(true)
      }
    }
  })

  const handleNext = () => {
    if (aleStatus !== null) {
      confirmMutation.mutate({
        data: {
          plan_year: planYear,
          ale_status: aleStatus
        }
      })
    }
  }

  const isSubmitting = confirmMutation.isPending

  if (confirmed && aleStatus !== null) {
    return (
      <div className='space-y-6'>
        <ACAReportingHeader planYear={planYear} />

        <div>
          <h2 className='text-xl text-gray-800'>
            Here's what you have to do next:
          </h2>
        </div>

        <DownloadAndFileInstructions formType={formType} />

        {onChangeToOptIn && (
          <div className='text-left'>
            <TextButton color='gray' size='small' onClick={onChangeToOptIn}>
              Changed your mind? Opt in here.
            </TextButton>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className='space-y-6'>
      <Banner type='info' title='Not eligible for Thatch filing'>
        You are not eligible, but Thatch can still provide your 1095 data in a CSV format.
      </Banner>

      <div className='bg-white rounded-lg border border-gray-200 p-6'>
        <div className='space-y-4'>
          <AleQuestionSection
            aleStatus={aleStatus}
            onAleStatusChange={setAleStatus}
            showFormTypeHint
          />

          {confirmMutation.isError && (
            <Banner type='critical' title='Unable to confirm ALE status'>
              {(confirmMutation.error as Error)?.message ?? 'An error occurred. Please try again.'}
            </Banner>
          )}

          <div className='flex justify-end'>
            <Button
              onClick={handleNext}
              disabled={aleStatus === null || isSubmitting}
              variant='primary'
            >
              {isSubmitting
                ? (
                  <span className='flex items-center gap-2'>
                    <Loader2Icon className='w-4 h-4 animate-spin' />
                    Confirming...
                  </span>
                  )
                : 'Next'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
